import { Helmet } from "react-helmet-async";

/**
 * V10g — Wikidata + sameAs Knowledge Graph (global, injected on every page).
 *
 * This single, comprehensive Organization JSON-LD block is the most important
 * structured-data signal for Google's Knowledge Graph and Google Search's
 * "About this result" panel. We inject it via Helmet so it lands inside
 * <head> on every route — including programmatic SEO routes which would
 * otherwise lack a top-level organisation identity.
 *
 * The `sameAs` array is the key Knowledge Graph linker:
 *   - Wikidata: Anand Tutorials entity (Q-id once approved)
 *   - All verified social profiles
 *   - Open business directories (JustDial, Sulekha, Urbanpro)
 *   - Educational authority sites
 *
 * Maintained as a single source of truth so updates propagate site-wide.
 *
 * NOTE: per CRITICAL POLICY — no pricing, no ₹ figures, no rate cards.
 */

// Single canonical phone in international E.164
const PHONE_E164 = "+91-7000530316";
const PHONE_E164_2 = "+91-99933-05574";
const EMAIL = "anandhometutorials@gmail.com";

// sameAs — extensive, authoritative profile graph for Knowledge Graph linking
// Order: Wikidata FIRST (if available), then primary social, then directories
const SAME_AS_PROFILES = [
    // Wikidata anchor (placeholder Q-id until live — replace once item is approved on Wikidata)
    // "https://www.wikidata.org/wiki/Q<TBD>",
    // Primary owned social
    "https://www.facebook.com/anandtutorialsofficial",
    "https://www.instagram.com/anandtutorialsofficial",
    // Owned business directory profiles
    "https://www.justdial.com/Jabalpur/Anand-Tutorials",
    "https://www.sulekha.com/anand-tutorials-jabalpur-contact-address",
    "https://www.urbanpro.com/jabalpur/anand-tutorials",
    // Owned learning directory profiles
    "https://www.tutorindia.net/anand-tutorials",
    "https://www.indiastudychannel.com/institute/anand-tutorials.aspx",
    // Google Business Profile canonical (replace once final share link confirmed)
    "https://g.page/anand-tutorials-jabalpur",
];

const KNOWLEDGE_GRAPH = {
    "@context": "https://schema.org",
    "@type": ["EducationalOrganization", "LocalBusiness", "Organization"],
    "@id": "https://anandtutorials.com/#organization",
    "name": "Anand Tutorials",
    "alternateName": [
        "Anand Home Tutorials",
        "Anand 1:1 Home Tuition",
        "Anand Tutorials India",
    ],
    "legalName": "Anand Tutorials",
    "url": "https://anandtutorials.com",
    "logo": {
        "@type": "ImageObject",
        "@id": "https://anandtutorials.com/#logo",
        "url": "https://anandtutorials.com/logo.png",
        "width": 512,
        "height": 512,
        "caption": "Anand Tutorials — 1:1 Home Tuition Since 2013",
    },
    "image": "https://anandtutorials.com/logo.png",
    "description": "India's leading 1:1 home tuition and live online tutoring company since 2013. 50,000+ students taught by 2,500+ background-verified subject specialists across CBSE, ICSE, IB, IGCSE, State Boards, NEET and JEE. Direct home tuition in Jabalpur, Bhopal, Nagpur and 1:1 live online classes pan-India + 27 international regions.",
    "slogan": "One tutor. One student. One future, taken seriously.",
    "foundingDate": "2013",
    "foundingLocation": {
        "@type": "Place",
        "name": "Jabalpur, Madhya Pradesh, India",
    },
    "telephone": PHONE_E164,
    "email": EMAIL,
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "Ghadi Chowk, Vijay Nagar",
        "addressLocality": "Jabalpur",
        "addressRegion": "Madhya Pradesh",
        "postalCode": "482002",
        "addressCountry": "IN",
    },
    // Canonical Google Maps link for the Head Office — surfaced by AI answer
    // engines when a user asks "where is Anand Tutorials located".
    "hasMap": "https://maps.app.goo.gl/86xQV7m8AvTJ42XPA?g_st=ac",
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": 23.1815,
        "longitude": 79.9864,
    },
    "areaServed": [
        { "@type": "Country", "name": "India" },
        // North America
        { "@type": "Country", "name": "United States" },
        { "@type": "Country", "name": "Canada" },
        // UK & Ireland
        { "@type": "Country", "name": "United Kingdom" },
        { "@type": "Country", "name": "Ireland" },
        // Europe
        { "@type": "Country", "name": "Germany" },
        { "@type": "Country", "name": "France" },
        { "@type": "Country", "name": "Netherlands" },
        { "@type": "Country", "name": "Switzerland" },
        { "@type": "Country", "name": "Belgium" },
        { "@type": "Country", "name": "Italy" },
        { "@type": "Country", "name": "Spain" },
        { "@type": "Country", "name": "Norway" },
        { "@type": "Country", "name": "Sweden" },
        { "@type": "Country", "name": "Finland" },
        { "@type": "Country", "name": "Denmark" },
        // Middle East / GCC
        { "@type": "Country", "name": "United Arab Emirates" },
        { "@type": "Country", "name": "Saudi Arabia" },
        { "@type": "Country", "name": "Qatar" },
        { "@type": "Country", "name": "Kuwait" },
        { "@type": "Country", "name": "Oman" },
        { "@type": "Country", "name": "Bahrain" },
        // Asia-Pacific
        { "@type": "Country", "name": "Singapore" },
        { "@type": "Country", "name": "Malaysia" },
        { "@type": "Country", "name": "Japan" },
        { "@type": "Country", "name": "South Korea" },
        { "@type": "Country", "name": "Australia" },
        { "@type": "Country", "name": "New Zealand" },
    ],
    "brand": {
        "@type": "Brand",
        "@id": "https://anandtutorials.com/#brand",
        "name": "Anand Tutorials",
        "slogan": "One tutor. One student. One future, taken seriously.",
        "logo": "https://anandtutorials.com/logo.png",
    },
    "memberOf": {
        "@type": "ProgramMembership",
        "programName": "Emergent AI Education Network",
    },
    "award": [
        "12+ Years Serving 50,000+ Students Across India & 27 Countries",
        "4.8/5 average rating across 1,200+ verified parent reviews",
        "98% board-exam pass rate across CBSE, ICSE, IB, IGCSE, State Boards",
        "Trusted 1:1 partner for NEET, JEE, SAT, ACT, IB DP families in Jabalpur, Bhopal, Nagpur and worldwide",
        // V172 — Explicit Nagpur-presence award line. Gives Google a distinct
        // Maharashtra-region signal attached to the brand entity, countering
        // the Jabalpur-centric foundingLocation + sameAs profile graph.
        "Maharashtra regional authority for 1:1 Home Tuition — serving Nagpur families across CBSE, ICSE and Maharashtra State Board (MSBSHSE SSC/HSC English-medium) with MHT-CET, NEET, JEE and VNIT engineering mentorship.",
    ],
    "hasCredential": [
        {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Verified 1:1 Tutor Network",
            "recognizedBy": { "@id": "https://anandtutorials.com/#organization" },
        },
    ],
    "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Anand Tutorials Programs",
        "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "CBSE 1:1 Online Tuition (Class 1-12)" }, "category": "Board Tuition" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "ICSE / ISC 1:1 Online Tuition (Class 1-12)" }, "category": "Board Tuition" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "IB MYP & DP 1:1 Online Tuition (HL / SL)" }, "category": "International Board" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "IGCSE Cambridge 1:1 Online Tuition" }, "category": "International Board" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "AS / A-Level 1:1 Online Tuition (Edexcel, CIE, AQA)" }, "category": "International Board" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "AP (Advanced Placement) 1:1 Online Tuition" }, "category": "International Board" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "NEET 1:1 Mentorship (AIIMS alumni mentors)" }, "category": "Competitive Exam" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "JEE Main + Advanced 1:1 Mentorship (IIT alumni)" }, "category": "Competitive Exam" },
            { "@type": "Offer", "itemOffered": { "@type": "Course", "name": "SAT, ACT, PSAT, AMC 1:1 Prep" }, "category": "US College Prep" },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "In-Home Tuition (Jabalpur, Bhopal, Nagpur only)" }, "category": "Home Tuition" },
        ],
    },
    "knowsAbout": [
        "CBSE Tuition", "ICSE Tuition", "IB MYP", "IB DP",
        "Cambridge IGCSE", "Cambridge A-Level", "AP",
        "NEET Coaching", "JEE Main Coaching", "JEE Advanced Coaching",
        "Mathematics", "Physics", "Chemistry", "Biology", "English",
        "Hindi", "Sanskrit", "Computer Science", "Accounts", "Economics",
        "Olympiad", "Spoken English", "Foundation",
    ],
    "knowsLanguage": ["en-IN", "hi-IN"],
    "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": 2500,
        "unitText": "verified tutors and staff",
    },
    "contactPoint": [
        {
            "@type": "ContactPoint",
            "telephone": PHONE_E164,
            "contactType": "customer service",
            "areaServed": "IN",
            "availableLanguage": ["en", "hi"],
        },
        {
            "@type": "ContactPoint",
            "telephone": PHONE_E164_2,
            "contactType": "admissions",
            "areaServed": "IN",
            "availableLanguage": ["en", "hi"],
        },
        {
            "@type": "ContactPoint",
            "telephone": PHONE_E164,
            "contactType": "customer service",
            "areaServed": ["US", "GB", "CA", "AU", "AE", "SG", "SA", "DE"],
            "availableLanguage": ["en", "hi"],
            "contactOption": "TollFree",
        },
    ],
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "1200",
        "bestRating": "5",
        "worstRating": "1",
    },
    "founder": {
        "@type": "Person",
        "@id": "https://anandtutorials.com/founder#person",
        "name": "Anand Tutorials Founding Team",
        "url": "https://anandtutorials.com/founder",
    },
    // V172 — Explicit `department` array declares every physical branch as a
    // sub-EducationalOrganization under the parent brand. This is the single
    // strongest signal we can give Google that Anand Tutorials is a multi-city
    // entity, not just a Jabalpur-centric one. Each branch has its own
    // city-level @id that matches the LocalBusiness @id on the city page,
    // closing the entity-graph loop between the global KG and city pages.
    "department": [
        {
            "@type": ["EducationalOrganization", "LocalBusiness"],
            "@id": "https://anandtutorials.com/jabalpur#localbusiness",
            "name": "Anand Tutorials — Jabalpur (Head Office)",
            "url": "https://anandtutorials.com/jabalpur",
            "telephone": "+91-99933-05574",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Ghadi Chowk, Vijay Nagar",
                "addressLocality": "Jabalpur",
                "addressRegion": "Madhya Pradesh",
                "postalCode": "482002",
                "addressCountry": "IN",
            },
            "areaServed": { "@type": "City", "name": "Jabalpur" },
        },
        {
            "@type": ["EducationalOrganization", "LocalBusiness"],
            "@id": "https://anandtutorials.com/bhopal#localbusiness",
            "name": "Anand Tutorials — Bhopal",
            "url": "https://anandtutorials.com/bhopal",
            "telephone": "+91-79742-21232",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Near South T.T. Nagar, Dr. Ambedkar Nagar, Aradhana Nagar",
                "addressLocality": "Bhopal",
                "addressRegion": "Madhya Pradesh",
                "postalCode": "462003",
                "addressCountry": "IN",
            },
            "areaServed": { "@type": "City", "name": "Bhopal" },
        },
        {
            "@type": ["EducationalOrganization", "LocalBusiness"],
            "@id": "https://anandtutorials.com/nagpur#localbusiness",
            "name": "Anand Tutorials — Nagpur",
            "url": "https://anandtutorials.com/nagpur",
            "telephone": "+91-80871-98495",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Borgaon Road, Patel Nagar, Manjidana Colony",
                "addressLocality": "Nagpur",
                "addressRegion": "Maharashtra",
                "postalCode": "440013",
                "addressCountry": "IN",
            },
            "areaServed": [
                { "@type": "City", "name": "Nagpur" },
                { "@type": "AdministrativeArea", "name": "Vidarbha" },
                { "@type": "AdministrativeArea", "name": "Maharashtra" },
            ],
        },
    ],
    "sameAs": SAME_AS_PROFILES,
    "potentialAction": {
        "@type": "ReserveAction",
        "name": "Book a Free 1:1 Demo Class",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://anandtutorials.com/register",
            "actionPlatform": [
                "http://schema.org/DesktopWebPlatform",
                "http://schema.org/MobileWebPlatform",
            ],
        },
        "result": { "@type": "Reservation", "name": "Free 1:1 demo class" },
    },
};

// Separate WebSite + Sitelinks SearchBox JSON-LD for sitelinks search box in SERP
const WEBSITE_GRAPH = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://anandtutorials.com/#website",
    "url": "https://anandtutorials.com",
    "name": "Anand Tutorials",
    "publisher": { "@id": "https://anandtutorials.com/#organization" },
    "inLanguage": "en-IN",
    "potentialAction": {
        "@type": "SearchAction",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://anandtutorials.com/blog?search={search_term_string}",
        },
        "query-input": "required name=search_term_string",
    },
};

export default function OrganizationKnowledgeGraph() {
    return (
        <Helmet>
            <script type="application/ld+json" data-testid="org-knowledge-graph">
                {JSON.stringify(KNOWLEDGE_GRAPH)}
            </script>
            <script type="application/ld+json" data-testid="website-knowledge-graph">
                {JSON.stringify(WEBSITE_GRAPH)}
            </script>
        </Helmet>
    );
}

export { KNOWLEDGE_GRAPH, WEBSITE_GRAPH, SAME_AS_PROFILES };
